// Phase 4c.7 — V-shape column resolution.
//
// The single new data primitive for the s-expr dashboard renderer. A
// `DashboardSpec` tile carries unresolved `ColumnRef`s (V-shape, no
// I/O). `useResolvedColumns` turns a tile's refs into their column
// data on demand: it resolves each ref to an engine `Dataset` via
// `resolveColumnRef` (Session binding lookup), materialises it, and
// returns the column-oriented cell arrays.
//
// One `useSuspenseQuery` per call (array in / array out) so the hook
// count is stable regardless of how many series/columns a tile has
// (React rules-of-hooks). Identical ref sets across tiles share the
// TanStack cache entry — this is the spec's "coalesce materialisation
// across tiles".

import { useSuspenseQuery } from '@tanstack/react-query';

import { NO_POS } from '#core/lib/expr/ast.ts';
import { projectColumnRef } from '#core/lib/expr/column-ref.ts';
import { useSession } from '#core/lib/expr/session/SessionContext.ts';
import { useMaterialize } from '#core/lib/expr/session/useMaterialize.ts';
import type { Cell, ColumnRef } from '#core/lib/expr/value.ts';

/**
 * Resolve + materialise a list of `ColumnRef`s to their cell arrays,
 * preserving order. Suspends until every ref is materialised; the
 * enclosing tile's `<Suspense>` shows its skeleton meanwhile.
 */
export function useResolvedColumns(refs: readonly ColumnRef[]): readonly (readonly Cell[])[] {
  const session = useSession();
  const materialize = useMaterialize();
  // Stable cache key — string-`ds` refs across tiles dedupe; inline
  // (Form-2) datasets fall back to a non-shared marker (rare in
  // dashboards, which use `(col binding col)`).
  const refKey = refs.map((r) => [typeof r.ds === 'string' ? r.ds : '<inline>', r.column, r.path] as const);
  const { data } = useSuspenseQuery({
    queryKey: ['dashboard-spec-columns', session.id, refKey],
    queryFn: async (): Promise<readonly (readonly Cell[])[]> =>
      Promise.all(
        refs.map(async (ref) => {
          // SSOT: resolve → materialise-if-lazy → per-cell `ref.path`
          // walk. This applies the struct sub-path so `(col ds s a)`
          // yields the leaf scalar cells, not the raw struct objects.
          const { cells } = await projectColumnRef(ref, session, NO_POS, materialize);
          return cells;
        }),
      ),
    // Fail fast — deterministic engine work; the default 3× retry +
    // backoff only freezes the tile skeleton for ~5s before the Error
    // Boundary trips. Mirrors `createChatSession.fetch` / `useMaterializedExpr`.
    retry: false,
  });
  return data;
}
